
import { useState } from "react";
import { Collection } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { Edit, Trash2, Wand2, Loader2, Play } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useCollectionResumeState } from "@/hooks/useCollectionResumeState";
import { useQueryClient } from '@tanstack/react-query';

interface CollectionCardActionsProps {
  collection: Collection;
  onEdit: (collection: Collection) => void;
  onDelete: (collection: Collection) => void;
  onRegenerateStart?: () => void;
}

export const CollectionCardActions = ({
  collection,
  onEdit,
  onDelete,
  onRegenerateStart,
}: CollectionCardActionsProps) => {
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const { toast } = useToast();
  const { data: resumeState, isLoading: resumeStateLoading } = useCollectionResumeState(collection.id);
  const queryClient = useQueryClient();

  const handleRegenerate = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      setIsRegenerating(true);
      
      const { data, error } = await supabase.functions.invoke('regenerate-collection', {
        body: {
          collectionId: collection.id
        }
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Regeneration Started",
        description: `AI generation has been restarted for ${collection.name}. You'll see progress on the collection card.`,
      });

      onRegenerateStart?.();

    } catch (error) {
      console.error('Error starting regeneration:', error);
      toast({
        title: "Error",
        description: "Failed to start regeneration. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleResume = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      setIsResuming(true);
      
      const { data, error } = await supabase.functions.invoke('resume-ai-generation_v2', {
        body: {
          collectionId: collection.id,
          force: true
        }
      });

      if (error) {
        throw error;
      }

      toast({
        title: "AI Generation Resumed",
        description: `Continuing AI generation for ${resumeState?.pendingCount || 0} remaining products in ${collection.name}.`,
      });

      onRegenerateStart?.();

      // Invalidate the resume state query to update the UI
      queryClient.invalidateQueries({ queryKey: ['collection-resume-state', collection.id] });

    } catch (error) {
      console.error('Error resuming AI generation:', error);
      toast({
        title: "Error",
        description: "Failed to resume AI generation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsResuming(false);
    }
  };

  return (
    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0"
        onClick={(e) => {
          e.stopPropagation();
          onEdit(collection);
        }}
        title="Edit"
      >
        <Edit className="h-3 w-3" />
      </Button>
      
      {/* Show Resume button if AI generation stopped prematurely, otherwise show Regenerate */}
      {resumeState?.canResume ? (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={handleResume}
          disabled={isResuming || resumeStateLoading}
          title={`Resume AI Generation (${resumeState.pendingCount} remaining)`}
        >
          {isResuming ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Play className="h-3 w-3" />
          )}
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={handleRegenerate}
          disabled={isRegenerating}
          title="Regenerate All"
        >
          {isRegenerating ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Wand2 className="h-3 w-3" />
          )}
        </Button>
      )}
      
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 text-destructive hover:text-destructive"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(collection);
        }}
        title="Delete"
      >
        <Trash2 className="h-3 w-3" />
      </Button>
    </div>
  );
};
